import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  PropsWithChildren,
} from "react";
import {
  ApolloCache,
  DefaultContext,
  MutationTuple,
  OperationVariables,
  useMutation,
} from "@apollo/client";
import { logEvent } from "../../../helpers/analytics";
import { 
  RecruitmentConsultationFormData,
  CompanyInformationFormData,
  ContactDetailsFormData,
  JobConsultationFormData,
} from "../schemas/recruitment-consultation.schema";
import { gql } from "../../../__generated__";
import {
  CreateRecruitmentConsultationTaskMutation,
  RecruitmentConsultationDto,
  Exact,
} from "../../../__generated__/graphql";

export const CREATE_RECRUITMENT_CONSULTATION_TASK = gql(`
  mutation createRecruitmentConsultationTask($recruitmentConsultationDto: RecruitmentConsultationDto!) {
    createRecruitmentConsultationTask(recruitmentConsultationDto: $recruitmentConsultationDto)
  }
`);

interface RecruitmentConsultationContextType {
  currentStep: number;
  isLoading: boolean;
  goToStep: (step: number) => void;
  nextStep: () => void;
  previousStep: () => void;
  submitForm: MutationTuple<
    CreateRecruitmentConsultationTaskMutation,
    Exact<{ recruitmentConsultationDto: RecruitmentConsultationDto }>,
    DefaultContext,
    ApolloCache<any>
  >[0];
  resetForm: () => void;
  updateCompanyInfo: (data: CompanyInformationFormData) => void;
  updateContactDetails: (data: ContactDetailsFormData) => void;
  updateJobConsultation: (data: JobConsultationFormData) => void;
  getAllFormData: () => Partial<RecruitmentConsultationFormData>;
  getCompanyInfo: () => Partial<CompanyInformationFormData>;
  getContactDetails: () => Partial<ContactDetailsFormData>;
  getJobConsultation: () => Partial<JobConsultationFormData>;
}

const RecruitmentConsultationContext =
  createContext<RecruitmentConsultationContextType | undefined>(undefined);

export const useRecruitmentConsultation =
  (): RecruitmentConsultationContextType => {
    const context = useContext(RecruitmentConsultationContext);
    if (!context) {
      throw new Error(
        "useRecruitmentConsultation must be used within a RecruitmentConsultationProvider"
      );
    }
    return context;
  };

export const RecruitmentConsultationProvider = ({
  children,
}: PropsWithChildren) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<Partial<RecruitmentConsultationFormData>>({});

  const [createRecruitmentConsultationTask, { loading }] = useMutation(
    CREATE_RECRUITMENT_CONSULTATION_TASK
  );

  const goToStep = useCallback((step: number) => {
    if (step >= 1 && step <= 3) {
      setCurrentStep(step);
      logEvent("recruitment_consultation_step_changed", { step });
    }
  }, []);

  const nextStep = useCallback(() => {
    if (currentStep < 3) {
      setCurrentStep((prev) => prev + 1);
      logEvent("recruitment_consultation_next_step", {
        step: currentStep + 1,
      });
    }
  }, [currentStep]);

  const previousStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
      logEvent("recruitment_consultation_previous_step", {
        step: currentStep - 1,
      });
    }
  }, [currentStep]);

  const resetForm = useCallback(() => {
    setCurrentStep(1);
    setFormData({});
    logEvent("recruitment_consultation_reset");
  }, []);

  const updateCompanyInfo = useCallback((data: CompanyInformationFormData) => {
    setFormData(prev => ({ ...prev, ...data }));
    logEvent("recruitment_consultation_company_info_updated");
  }, []);

  const updateContactDetails = useCallback((data: ContactDetailsFormData) => {
    setFormData(prev => ({ ...prev, ...data }));
    logEvent("recruitment_consultation_contact_details_updated");
  }, []);

  const updateJobConsultation = useCallback((data: JobConsultationFormData) => {
    setFormData(prev => ({ ...prev, ...data }));
    logEvent("recruitment_consultation_job_consultation_updated");
  }, []);

  const getAllFormData = useCallback(() => {
    return formData;
  }, [formData]);

  const getCompanyInfo = useCallback((): Partial<CompanyInformationFormData> => {
    const { organization, roleInOrganization, industry, companySize } = formData;
    return { organization, roleInOrganization, industry, companySize };
  }, [formData]);

  const getContactDetails = useCallback((): Partial<ContactDetailsFormData> => {
    const { email, phone } = formData;
    return { email, phone };
  }, [formData]);

  const getJobConsultation = useCallback((): Partial<JobConsultationFormData> => {
    const { 
      jobTitle, 
      salaryRange, 
      employmentType, 
      duration, 
      location, 
      expectedStartDate 
    } = formData;
    return { 
      jobTitle, 
      salaryRange, 
      employmentType, 
      duration, 
      location, 
      expectedStartDate 
    };
  }, [formData]);

  const contextValue: RecruitmentConsultationContextType = {
    currentStep,
    isLoading: loading,
    goToStep,
    nextStep,
    previousStep,
    submitForm: createRecruitmentConsultationTask,
    resetForm,
    updateCompanyInfo,
    updateContactDetails,
    updateJobConsultation,
    getAllFormData,
    getCompanyInfo,
    getContactDetails,
    getJobConsultation,
  };

  return (
    <RecruitmentConsultationContext.Provider value={contextValue}>
      {children}
    </RecruitmentConsultationContext.Provider>
  );
};
